import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { SocialIconList } from '@powdr/components';
import { EmailServices, FormSilverpopRequiredFields } from '@powdr/constants';
import { isValidEmail } from '@powdr/utils';
import { useStaticData } from '@powdr/web/src/hooks';

import {
  EmailSignupContainer,
  SocialIconListWrapper,
  FormContainer,
  SignupForm,
  FormLabel,
  EmailInputField,
  SubmitButton,
  CaptchaAlignmentWrapper,
  InputSubmitWrapper,
  LabelInputWrapper,
  StyledReCAPTCHA,
} from './styles';

export const EmailSignup = ({
  emailServiceName = EmailServices.SILVERPOP,
  emailFormID = null,
  emailServiceFormID = null,
  emailServiceUrl = '',
  formLabel = 'Sign Up for Powdr Alerts and Deals',
  isHideSocialButtons = false,
}) => {
  const [email, setEmail] = useState('');
  const [isUserVerified, setUserVerified] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle');

  const {
    reCaptchaDBPropertyName,
    reCaptchaPublicKey,
    property,
    socialIcons,
  } = useStaticData();

  const normalizeEmailService = (name = '') => (
    name?.toLowerCase()?.replace(/[\s-]/g, '_') || ''
  );

  const isActiveCampaignService = (name, url = '') => {
    const normalized = normalizeEmailService(name);

    return normalized === EmailServices.ACTIVE_CAMPAIGN
      || normalized?.includes('active')
      || url?.includes('activehosted.com');
  };

  const consentDate = format(new Date(), 'MM/dd/yyyy');
  const isActiveCampaign = isActiveCampaignService(emailServiceName, emailServiceUrl);
  const validateEmail = () => isValidEmail(email);

  const emailUpdated = (e) => {
    setEmail(e.target.value);

    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
    }
  };

  const submitActiveCampaign = (form) => {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      params.append(key, value);
    });

    const script = document.createElement('script');

    script.src = `${emailServiceUrl}?${params.toString()}&jsonp=true`;

    script.onload = () => {
      setSubmitStatus('success');
      localStorage.setItem('email', email);
      script.remove();
    };

    script.onerror = () => {
      setSubmitStatus('error');
      script.remove();
    };

    document.body.appendChild(script);

    setTimeout(() => {
      if (document.body.contains(script)) {
        script.remove();
      }
    }, 10000);
  };

  const handleSubmit = (e) => {
    if (!isUserVerified || !validateEmail() || submitStatus === 'submitting') {
      e.preventDefault();
      return;
    }

    localStorage.setItem('email', email);

    // Silverpop hosted forms expect a native POST and a full-page response.
    if (!isActiveCampaign) {
      return;
    }

    e.preventDefault();
    setSubmitStatus('submitting');
    submitActiveCampaign(e.currentTarget);
  };

  if (submitStatus === 'success') {
    return (
      <EmailSignupContainer isHideSocialButtons={isHideSocialButtons}>
        <FormContainer>
          <p role="status">Thanks! You're signed up.</p>
        </FormContainer>
      </EmailSignupContainer>
    );
  }

  if (!emailServiceName) return null;

  return (
    <EmailSignupContainer isHideSocialButtons={isHideSocialButtons}>
      <FormContainer>
        <SignupForm
          onSubmit={handleSubmit}
          name={isActiveCampaign ? 'Email Signup Form' : 'silverpopForm'}
          action={emailServiceUrl}
          method="POST"
        >
          {(!isHideSocialButtons) && (
            <SocialIconListWrapper>
              {socialIcons.map((iconSet) => (
                <SocialIconList
                  key={iconSet.id}
                  parent="email-signup"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...iconSet}
                />
              ))}
            </SocialIconListWrapper>
          )}

          <LabelInputWrapper>
            <FormLabel htmlFor="field-email">
              {formLabel}
            </FormLabel>

            <CaptchaAlignmentWrapper>
              <InputSubmitWrapper>
                <EmailInputField
                  id="field-email"
                  name={isActiveCampaign ? 'email' : FormSilverpopRequiredFields.EMAIL}
                  type="email"
                  placeholder="E-mail address"
                  value={email}
                  onChange={emailUpdated}
                />

                <SubmitButton
                  type="submit"
                  value={submitStatus === 'submitting' ? '...' : 'Go'}
                  disabled={
                    !isUserVerified
                    || !validateEmail()
                    || submitStatus === 'submitting'
                  }
                />
              </InputSubmitWrapper>

              {validateEmail() && (
                <StyledReCAPTCHA
                  sitekey={reCaptchaPublicKey}
                  theme="light"
                  size="normal"
                  onChange={() => setUserVerified(true)}
                  onExpired={() => setUserVerified(false)}
                />
              )}
            </CaptchaAlignmentWrapper>

            {submitStatus === 'error' && (
              <p role="alert">
                Something went wrong. Please try again.
              </p>
            )}
          </LabelInputWrapper>

          {isActiveCampaign && (
            <>
              <input type="hidden" name="u" value={emailServiceFormID || ''} />
              <input type="hidden" name="f" value={emailServiceFormID || ''} />
              <input type="hidden" name="s" />
              <input type="hidden" name="c" value="0" />
              <input type="hidden" name="m" value="0" />
              <input type="hidden" name="act" value="sub" />
              <input type="hidden" name="v" value="2" />
              <input type="hidden" name="or" value={emailFormID || ''} />
            </>
          )}

          {!isActiveCampaign && (
            <>
              <input
                name="formSourceName"
                type="hidden"
                value="StandardForm"
                autoComplete="off"
              />
              <input
                name="sp_exp"
                type="hidden"
                value="yes"
                autoComplete="off"
              />
            </>
          )}

          <input
            name={`${property} Express Consent Date`}
            type="hidden"
            value={consentDate}
            autoComplete="off"
          />

          <input
            name={`${property} Express Consent Source`}
            type="hidden"
            value={`${reCaptchaDBPropertyName} Email Signup`}
            autoComplete="off"
          />
        </SignupForm>
      </FormContainer>
    </EmailSignupContainer>
  );
};

EmailSignup.propTypes = {
  id: PropTypes.string.isRequired,
  emailServiceName: PropTypes.string.isRequired,
  emailFormID: PropTypes.string,
  emailServiceFormID: PropTypes.string,
  emailServiceUrl: PropTypes.string,
  isHideSocialButtons: PropTypes.bool,
  formLabel: PropTypes.string,
};
